import { syntheticEntryQuery } from '../../../shared/content-media/content-media';
import { ContentChanges, ContentViewer } from '../../../shared/content-media/ContentMedia';
import {
  priorReview,
  reviewDecisionLabel,
  ReviewSummary,
} from '../../../shared/content-media/ReviewSummary';
import {
  exactSubmittedClaims,
  SubmittedReviewContent,
} from '../../../shared/product-review/consumer/submitted-content';
import { useSynLive } from '../../../shared/product-review/session/ProductReviewSession';
import '../../../shared/product-review/ui/product-review.css';

/** 08 审核中心 — the confirmed queue and decision workbench, driven by durable 08 reads. */
export function SynLiveReviewQueue() {
  const live = useSynLive();
  const selected = live.selected;
  const prior = priorReview(live.cases, selected);
  // There is no adapter until a session exists. Guarding here keeps "not logged in" from being
  // reported as an unconfirmed server result.
  const ready = live.port !== null && live.auth !== null;
  const refreshQueue = () => {
    if (!ready) return;
    live.clearCase();
    void live.run(
      '读取审核队列',
      async () => {
        const outcome = await live.port!.listCases({ session: live.session });
        if (outcome.kind !== 'OK') return outcome;
        // THE 08 QUEUE NOW CARRIES TWO SUBJECT KINDS AND THIS PANEL REVIEWS ONE. The server returns
        // every case this account may see, because "what may I review" is one question; this panel is
        // the 06 product surface's, so it shows the product models and the local assets are shown by
        // the asset panel beside it. Filtering here rather than on the server keeps the server's
        // answer honest — a queue narrowed by the caller would be a second place the scope rule lives.
        return {
          kind: 'OK' as const,
          value: outcome.value.filter((item) => item.submitted.subjectKind === 'PRODUCT_MODEL'),
        };
      },
      live.setCases,
    );
  };
  return (
    <>
      <nav className="provider-breadcrumb" aria-label="面包屑">
        <a href={`/demo/governance-review/queue${syntheticEntryQuery()}`}>⌂ 审核中心</a>
        <span>/</span>
        <strong>{selected ? selected.caseId : '审核队列'}</strong>
      </nav>
      <div className="provider-products-layout cm-review-layout">
        <aside className="provider-product-list portal-card" aria-label="审核队列">
          <div className="provider-list-heading">
            <h2>
              审核队列 <small>（{live.cases.length}）</small>
            </h2>
            <button className="portal-button" disabled={live.busy || !ready} onClick={refreshQueue}>
              刷新队列
            </button>
          </div>
          <p className="provider-fixture-note">
            队列只展示当前账号获准的 08 记录。空队列不代表已发布。
          </p>
          <div className="provider-products-scroll">
            {live.cases.map((item) => (
              <button
                className={`provider-list-product ${item.caseId === selected?.caseId ? 'is-selected' : ''}`}
                key={item.caseId}
                disabled={live.blocked}
                onClick={() => live.chooseCase(item)}
              >
                <div className="provider-image-placeholder">
                  <small>08</small>
                </div>
                <div>
                  <strong>{exactSubmittedClaims(item)?.modelName ?? '提交名称不可读取'}</strong>
                  <small>{item.caseId}</small>
                  <span>修订 {item.submitted.revision}</span>
                  <span>{reviewDecisionLabel(item.decision?.outcome)}</span>
                </div>
              </button>
            ))}
            {live.cases.length === 0 && (
              <div className="provider-empty">
                <h3>队列为空</h3>
                <p>请先在厂商端提交一个不可变修订，或刷新队列。</p>
              </div>
            )}
          </div>
        </aside>
        <article className="provider-product-detail portal-card">
          {!selected ? (
            <>
              <h1>受理精确提交</h1>
              <p>
                08
                没有自动发现提交的契约：受理必须抄录厂商端读回的精确不可变版本，不接受“最新版本”。
              </p>
              <fieldset disabled={live.blocked} className="provider-syn-fieldset">
                <label>
                  审核编号{' '}
                  <input
                    aria-label="审核编号"
                    value={live.caseId}
                    onChange={(event) => live.setCaseId(event.target.value)}
                  />
                </label>
                {(
                  ['productId', 'revision', 'digest', 'country', 'supplier', 'reviewScope'] as const
                ).map((key) => (
                  <label key={key}>
                    {key}{' '}
                    <input
                      aria-label={`受理 ${key}`}
                      value={live.reference[key]}
                      onChange={(event) =>
                        live.setReference({
                          ...live.reference,
                          [key]:
                            key === 'revision' ? Number(event.target.value) : event.target.value,
                        })
                      }
                    />
                  </label>
                ))}
                <button
                  className="portal-button"
                  disabled={!ready || !live.caseId}
                  onClick={() =>
                    live.mutate(
                      '受理提交',
                      (idempotencyKey) =>
                        live.port!.receive({
                          session: live.session,
                          idempotencyKey,
                          caseId: live.caseId,
                          revision: live.reference,
                        }),
                      live.applyCase,
                    )
                  }
                >
                  受理精确提交
                </button>
              </fieldset>
            </>
          ) : (
            <>
              <ReviewSummary
                review={selected}
                name={exactSubmittedClaims(selected)?.modelName ?? '提交名称不可读取'}
                decisionTarget="product-review-decision"
              />
              <ContentChanges
                before={prior?.submitted.immutableContent}
                after={selected.submitted.immutableContent}
                beforeRevision={prior?.submitted.revision}
                afterRevision={selected.submitted.revision}
              />
              <SubmittedReviewContent review={selected} />
              <ContentViewer
                owner="06"
                auth={{ session: live.session }}
                reference={{
                  subjectId: selected.submitted.subjectId,
                  revision: selected.submitted.revision,
                  digest: selected.submitted.digest,
                  view: 'submitted',
                  caseId: selected.caseId,
                }}
              />
              <section className="cm-decision" id="product-review-decision" tabIndex={-1}>
                <h2>审核决定</h2>
                <p>
                  当前结论：{reviewDecisionLabel(selected.decision?.outcome)} · 决定编号{' '}
                  {selected.decision?.decisionId ?? '无'}
                </p>
                {selected.decision && <p>审核意见：{selected.decision.reason}</p>}
                <fieldset
                  disabled={live.blocked || selected.decision !== null}
                  className="provider-syn-fieldset"
                >
                  <label>
                    决定编号{' '}
                    <input
                      aria-label="决定编号"
                      value={live.decisionId}
                      onChange={(event) => live.setDecisionId(event.target.value)}
                    />
                  </label>
                  <label>
                    审核意见{' '}
                    <textarea
                      aria-label="审核意见"
                      value={live.reason}
                      onChange={(event) => live.setReason(event.target.value)}
                    />
                  </label>
                  <div className="provider-syn-actions">
                    {(['CHANGES_REQUESTED', 'ACCEPTED'] as const).map((outcome) => (
                      <button
                        className="portal-button"
                        key={outcome}
                        disabled={
                          !live.decisionId ||
                          !live.reason ||
                          (outcome === 'ACCEPTED' && exactSubmittedClaims(selected) === null)
                        }
                        onClick={() =>
                          live.mutate(
                            outcome === 'ACCEPTED' ? '审核通过' : '退回修改',
                            (idempotencyKey) =>
                              live.port!.decide({
                                session: live.session,
                                idempotencyKey,
                                caseId: selected.caseId,
                                revision: selected.submitted,
                                expectedVersion: selected.version,
                                decisionId: live.decisionId,
                                outcome,
                                reason: live.reason,
                              }),
                            live.applyCase,
                          )
                        }
                      >
                        {outcome === 'ACCEPTED' ? '审核通过' : '退回修改'}
                      </button>
                    ))}
                  </div>
                </fieldset>
              </section>
              <section>
                <h2>Owner 发布与精确读回</h2>
                <p>
                  审核通过只产生审核决定。发布由服务端独立 Owner
                  身份执行；此处不能把状态直接改成已发布。
                </p>
                <div className="provider-syn-actions">
                  <button
                    className="portal-button product-demo-secondary"
                    disabled={live.blocked}
                    onClick={live.clearCase}
                  >
                    受理另一提交版本
                  </button>
                  <button
                    className="portal-button"
                    disabled={live.blocked || selected.decision?.outcome !== 'ACCEPTED'}
                    onClick={() =>
                      live.mutate(
                        '请求 Owner 发布',
                        (idempotencyKey) =>
                          live.port!.publish({
                            session: live.session,
                            idempotencyKey,
                            submission: selected.submitted,
                            decisionId: selected.decision!.decisionId,
                          }),
                        () => {
                          live.setNotice(
                            'Owner 已确认发布。请点击“核对发布与精确读回”由服务端独立确认读回。',
                          );
                        },
                      )
                    }
                  >
                    请求 Owner 发布
                  </button>
                  <button
                    className="portal-button"
                    disabled={live.blocked}
                    onClick={() =>
                      live.mutate(
                        '核对发布与精确读回',
                        (idempotencyKey) =>
                          live.port!.observe({
                            session: live.session,
                            idempotencyKey,
                            caseId: selected.caseId,
                            revision: selected.submitted,
                            expectedVersion: selected.version,
                          }),
                        live.applyCase,
                      )
                    }
                  >
                    核对发布与精确读回
                  </button>
                  <button
                    className="portal-button product-demo-secondary"
                    disabled={live.readBlocked || !ready}
                    onClick={() => {
                      if (!ready) return;
                      void live.run(
                        '读取审核记录',
                        () =>
                          live.port!.readCase({
                            session: live.session,
                            caseId: selected.caseId,
                            ...selected.submitted,
                          }),
                        live.applyCase,
                      );
                    }}
                  >
                    数据库读回审核记录
                  </button>
                </div>
                <p>
                  Owner：{selected.observation?.ownerStatus ?? 'UNKNOWN'} · 发布版本{' '}
                  {selected.observation?.ownerPublishedVersion ?? 'UNKNOWN'}
                </p>
                <p>
                  精确读回：{selected.observation?.readbackStatus ?? 'UNKNOWN'} · 匹配确认{' '}
                  {selected.observation?.exactReadbackConfirmed === true
                    ? '是（服务端确认）'
                    : '未确认'}
                </p>
              </section>
            </>
          )}
        </article>
      </div>
    </>
  );
}
